import { useMemo, useState } from 'react';
import {
  FileText,
  Clock,
  CheckCircle,
  AlertCircle,
  Calendar,
  XCircle,
  Loader2,
  ExternalLink,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useCurrentPatient } from '@/hooks/useCurrentPatient';
import {
  usePatientFormsAssignments,
  useRequestPortalFormLink,
  type PatientFormsAssignment,
  type FormsAssignmentStatus,
} from '@/hooks/usePatientFormsAssignments';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { publicUrlFromAssignmentMetadata } from '@/lib/formsUrls';

/**
 * Patient portal questionnaires page.
 * Active rows link to forms.clinio.dk for fill-out (cached metadata or edge function).
 */
export function PortalQuestionnaires() {
  const { t } = useTranslation();
  const { data: patient, isLoading: patientLoading } = useCurrentPatient();
  const {
    data: assignments,
    isLoading: assignmentsLoading,
    error,
  } = usePatientFormsAssignments(patient?.id);

  const { active, completed, terminal } = useMemo(() => {
    if (!assignments) return { active: [], completed: [], terminal: [] };
    return {
      active: assignments.filter((a) => a.status === 'sent' || a.status === 'reminded'),
      completed: assignments.filter((a) => a.status === 'completed'),
      terminal: assignments.filter((a) => a.status === 'expired' || a.status === 'cancelled'),
    };
  }, [assignments]);

  if (patientLoading || assignmentsLoading) {
    return (
      <div className="flex min-h-[400px] items-center justify-center">
        <div className="text-center">
          <Loader2 className="mx-auto h-8 w-8 animate-spin text-indigo-600 dark:text-indigo-400" />
          <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
            {t('questionnaires.portal_loading')}
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <PageHeader t={t} />
        <ErrorAlert message={t('questionnaires.portal_error')} />
      </div>
    );
  }

  const isEmpty = active.length === 0 && completed.length === 0 && terminal.length === 0;

  return (
    <div className="space-y-6">
      <PageHeader t={t} />

      {isEmpty && (
        <div className="rounded-lg border-2 border-dashed border-gray-300 bg-white p-12 text-center dark:border-gray-700 dark:bg-gray-800">
          <FileText className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-600" aria-hidden="true" />
          <h3 className="mt-4 text-lg font-medium text-gray-900 dark:text-white">
            {t('questionnaires.portal_empty_title')}
          </h3>
          <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
            {t('questionnaires.portal_empty_message')}
          </p>
        </div>
      )}

      {active.length > 0 && (
        <section aria-labelledby="active-section">
          <h2
            id="active-section"
            className="mb-4 text-lg font-semibold text-gray-900 dark:text-white"
          >
            {t('questionnaires.portal_section_active')} ({active.length})
          </h2>
          <ul className="space-y-3" role="list">
            {active.map((a) => (
              <li key={a.id}>
                <AssignmentCard assignment={a} t={t} />
              </li>
            ))}
          </ul>
        </section>
      )}

      {completed.length > 0 && (
        <section aria-labelledby="completed-section">
          <h2
            id="completed-section"
            className="mb-4 text-lg font-semibold text-gray-900 dark:text-white"
          >
            {t('questionnaires.portal_section_completed')} ({completed.length})
          </h2>
          <ul className="space-y-3" role="list">
            {completed.map((a) => (
              <li key={a.id}>
                <AssignmentCard assignment={a} t={t} />
              </li>
            ))}
          </ul>
        </section>
      )}

      {terminal.length > 0 && (
        <section aria-labelledby="terminal-section">
          <h2
            id="terminal-section"
            className="mb-4 text-lg font-semibold text-gray-900 dark:text-white"
          >
            {t('questionnaires.portal_section_terminal')} ({terminal.length})
          </h2>
          <ul className="space-y-3" role="list">
            {terminal.map((a) => (
              <li key={a.id}>
                <AssignmentCard assignment={a} t={t} />
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}

// ---- Sub-components ----

function PageHeader({ t }: { t: ReturnType<typeof useTranslation>['t'] }) {
  return (
    <div>
      <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
        {t('questionnaires.portal_heading')}
      </h1>
      <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
        {t('questionnaires.portal_subtitle')}
      </p>
    </div>
  );
}

const STATUS_CONFIG: Record<
  FormsAssignmentStatus,
  {
    Icon: React.ElementType;
    color: string;
    bgColor: string;
    borderColor: string;
    label: string;
  }
> = {
  sent: {
    Icon: Clock,
    color: 'text-yellow-600 dark:text-yellow-400',
    bgColor: 'bg-yellow-50 dark:bg-yellow-900/20',
    borderColor: 'border-yellow-200 dark:border-yellow-800',
    label: 'portal_status_sent',
  },
  reminded: {
    Icon: AlertCircle,
    color: 'text-orange-600 dark:text-orange-400',
    bgColor: 'bg-orange-50 dark:bg-orange-900/20',
    borderColor: 'border-orange-200 dark:border-orange-800',
    label: 'portal_status_reminded',
  },
  completed: {
    Icon: CheckCircle,
    color: 'text-emerald-600 dark:text-emerald-400',
    bgColor: 'bg-emerald-50 dark:bg-emerald-900/20',
    borderColor: 'border-emerald-200 dark:border-emerald-800',
    label: 'portal_status_completed',
  },
  expired: {
    Icon: XCircle,
    color: 'text-red-600 dark:text-red-400',
    bgColor: 'bg-red-50 dark:bg-red-900/20',
    borderColor: 'border-red-200 dark:border-red-800',
    label: 'portal_status_expired',
  },
  cancelled: {
    Icon: XCircle,
    color: 'text-gray-500 dark:text-gray-400',
    bgColor: 'bg-gray-50 dark:bg-gray-900/20',
    borderColor: 'border-gray-200 dark:border-gray-700',
    label: 'portal_status_cancelled',
  },
};

function AssignmentCard({
  assignment,
  t,
}: {
  assignment: PatientFormsAssignment;
  t: ReturnType<typeof useTranslation>['t'];
}) {
  const [linkError, setLinkError] = useState<string | null>(null);
  const { mutate: requestLink, isPending: linkPending } = useRequestPortalFormLink();

  const cfg = STATUS_CONFIG[assignment.status] ?? STATUS_CONFIG['sent'];
  const { Icon } = cfg;

  const templateName =
    (assignment.template_summary as { name?: string } | null)?.name ??
    assignment.forms_template_id;

  const isActive = assignment.status === 'sent' || assignment.status === 'reminded';
  const cachedUrl = publicUrlFromAssignmentMetadata(assignment.metadata);

  function openUrl(url: string) {
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  function handleOpen() {
    if (cachedUrl) {
      openUrl(cachedUrl);
      return;
    }

    setLinkError(null);
    requestLink(assignment.id, {
      onSuccess(result) {
        if (result.ok && result.url) {
          openUrl(result.url);
        } else {
          setLinkError(t('questionnaires.portal_open_pending_error'));
        }
      },
      onError() {
        setLinkError(t('questionnaires.portal_open_error'));
      },
    });
  }

  const scoreDisplay = extractScoreDisplay(assignment.score);

  return (
    <article
      className={`rounded-lg border ${cfg.borderColor} ${cfg.bgColor} p-4 transition-shadow hover:shadow-md`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Icon className={`h-5 w-5 shrink-0 ${cfg.color}`} aria-hidden="true" />
            <span className={`text-sm font-medium ${cfg.color}`}>
              {t(`questionnaires.${cfg.label}`)}
            </span>
          </div>

          <h3 className="mt-2 font-semibold text-gray-900 dark:text-white truncate">
            {templateName}
          </h3>

          <dl className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600 dark:text-gray-400">
            {assignment.sent_at && (
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4 shrink-0" aria-hidden="true" />
                <dt className="sr-only">{t('questionnaires.portal_sent_label')}</dt>
                <dd>
                  {t('questionnaires.portal_sent_label')} {formatDate(assignment.sent_at)}
                </dd>
              </div>
            )}

            {assignment.due_at && isActive && (
              <div className="flex items-center gap-1">
                <Clock className="h-4 w-4 shrink-0" aria-hidden="true" />
                <dt className="sr-only">{t('questionnaires.portal_due_label')}</dt>
                <dd>
                  {t('questionnaires.portal_due_label')} {formatDate(assignment.due_at)}
                </dd>
              </div>
            )}

            {assignment.completed_at && (
              <div className="flex items-center gap-1">
                <CheckCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
                <dt className="sr-only">{t('questionnaires.portal_completed_label')}</dt>
                <dd>
                  {t('questionnaires.portal_completed_label')}{' '}
                  {formatDate(assignment.completed_at)}
                </dd>
              </div>
            )}
          </dl>

          {/* Score display for completed rows (read-only mirror from Forms via webhook) */}
          {assignment.status === 'completed' && scoreDisplay !== null && (
            <p className="mt-2 text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('questionnaires.portal_score_label')}: {scoreDisplay}
            </p>
          )}

          {linkError && (
            <p role="alert" className="mt-2 text-sm text-amber-700 dark:text-amber-400">
              {linkError}
            </p>
          )}
        </div>

        {isActive && (
          cachedUrl ? (
            <a
              href={cachedUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`${t('questionnaires.portal_open_label')} — ${templateName}`}
              className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:bg-indigo-500 dark:hover:bg-indigo-400"
            >
              <ExternalLink className="h-4 w-4" aria-hidden="true" />
              {t('questionnaires.portal_open_label')}
            </a>
          ) : (
            <button
              type="button"
              onClick={handleOpen}
              disabled={linkPending}
              aria-label={`${t('questionnaires.portal_open_label')} — ${templateName}`}
              className="shrink-0 inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-indigo-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-indigo-500 dark:hover:bg-indigo-400"
            >
              {linkPending ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <ExternalLink className="h-4 w-4" aria-hidden="true" />
              )}
              {t('questionnaires.portal_open_label')}
            </button>
          )
        )}
      </div>
    </article>
  );
}

// ---- Helpers ----

function formatDate(dateStr: string): string {
  return new Date(dateStr).toLocaleDateString('da-DK', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function extractScoreDisplay(score: unknown): string | null {
  if (score === null || score === undefined) return null;
  if (typeof score === 'number') return String(score);
  if (typeof score === 'object' && score !== null) {
    const s = score as Record<string, unknown>;
    if (typeof s['score_total'] === 'number') return String(s['score_total']);
    if (typeof s['total'] === 'number') return String(s['total']);
    if (typeof s['score'] === 'number') return String(s['score']);
  }
  return null;
}
